import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css, SerializedStyles } from '@emotion/core'
import { Popup } from '@/components/atoms/popup'
import { Checkbox } from '@/components/atoms/checkbox'
import { CompanionType, AgeGroupType } from '@/models/survey/filter-types'

type Props = {
  isOpen: boolean
  onClose: () => void
  companion: CompanionType[]
  ageGroup: AgeGroupType[]
  onChange: (companion: CompanionType[], ageGroup: AgeGroupType[]) => void
  customStyle?: SerializedStyles
}

const COMPANION_TYPES: CompanionType[] = ['none', 'alone', 'family', 'friend', 'partner', 'coworker', 'other']
const AGE_GROUP_TYPES: AgeGroupType[] = ['none', '10', '20', '30', '40', '50', '60', '70']

export const FilterPopup: React.FC<Props> = ({ isOpen, onClose, companion, ageGroup, onChange, customStyle }) => {
  const { t } = useTranslation()

  const getCompanionLabel = (value: CompanionType): string => {
    return t(`SurveyFilter.CompanionOptions.${value}`)
  }

  const getAgeGroupLabel = (value: AgeGroupType): string => {
    return t(`SurveyFilter.AgeGroupOptions.${value}`)
  }

  // すべて選択されているかチェック
  const isAllCompanionSelected = useMemo(() => {
    return COMPANION_TYPES.every(type => companion.includes(type))
  }, [companion])

  const isAllAgeGroupSelected = useMemo(() => {
    return AGE_GROUP_TYPES.every(type => ageGroup.includes(type))
  }, [ageGroup])

  const handleCompanionToggle = (value: CompanionType) => {
    const newCompanion = companion.includes(value) ? companion.filter(c => c !== value) : [...companion, value]
    onChange(newCompanion, ageGroup)
  }

  const handleAgeGroupToggle = (value: AgeGroupType) => {
    const newAgeGroup = ageGroup.includes(value) ? ageGroup.filter(a => a !== value) : [...ageGroup, value]
    onChange(companion, newAgeGroup)
  }

  const handleToggleAllCompanion = () => {
    if (isAllCompanionSelected) {
      onChange([], ageGroup)
    } else {
      onChange(COMPANION_TYPES, ageGroup)
    }
  }

  const handleToggleAllAgeGroup = () => {
    if (isAllAgeGroupSelected) {
      onChange(companion, [])
    } else {
      onChange(companion, AGE_GROUP_TYPES)
    }
  }

  return (
    <Popup isOpen={isOpen} onClose={onClose} customStyle={customStyle}>
      <div css={filterPopupStyle}>
        <div>
          <div className="title-block">
            <div>{t('SurveyFilter.CompanionLabel')}</div>
            <div onClick={handleToggleAllCompanion}>{isAllCompanionSelected ? t('Deselect All') : t('Select All')}</div>
          </div>
          <div className="item-block">
            {COMPANION_TYPES.map(value => (
              <Checkbox
                key={value}
                value={companion.includes(value)}
                label={getCompanionLabel(value)}
                onChange={() => handleCompanionToggle(value)}
              />
            ))}
          </div>
        </div>

        <div>
          <div className="title-block">
            <div>{t('SurveyFilter.AgeGroupLabel')}</div>
            <div onClick={handleToggleAllAgeGroup}>{isAllAgeGroupSelected ? t('Deselect All') : t('Select All')}</div>
          </div>
          <div className="item-block">
            {AGE_GROUP_TYPES.map(value => (
              <Checkbox
                key={value}
                value={ageGroup.includes(value)}
                label={getAgeGroupLabel(value)}
                onChange={() => handleAgeGroupToggle(value)}
              />
            ))}
          </div>
        </div>
      </div>
    </Popup>
  )
}

const filterPopupStyle = css({
  padding: 16,
  width: 280,
  '> div': {
    paddingTop: 16,
    '&:first-of-type': { paddingTop: 0 },
  },
  '.title-block': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '> div:first-of-type': { fontSize: '14px !important' },
    '> div:last-of-type': { fontSize: '10px !important', cursor: 'pointer' },
  },
  '.item-block': {
    paddingTop: 8,
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: 8,
    '> div': { width: '50%' },
  },
})
